import ffmpegCoreURL from "../vendor/ffmpeg-core.js?url";
import ffmpegClassWorkerURL from "../vendor/ffmpeg-class-worker.js?url";
import ffmpegCoreWasmURL from "../vendor/ffmpeg-core.wasm?url";

interface FFmpegCoreUrls {
  classWorkerURL: string;
  workerURL: string;
  coreData: Uint8Array;
  wasmBinary: Uint8Array;
}

interface CachedFFmpegCoreAssets {
  classWorkerURL: string;
  workerURL: string;
  coreData: ArrayBuffer;
  wasmBinary: ArrayBuffer;
}

let cachedCoreUrlsPromise: Promise<CachedFFmpegCoreAssets> | null = null;
let warmupScheduled = false;
const POST_LOAD_WARMUP_DELAY_MS = 1500;

function resolveAbsoluteAssetUrl(url: string): string {
  if (typeof window === "undefined") {
    return url;
  }

  return new URL(url, window.location.href).href;
}

async function fetchAssetBuffer(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(`Failed to fetch FFmpeg asset ${url}: ${response.status}`);
  }

  return await response.arrayBuffer();
}

async function warmAsset(url: string): Promise<void> {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(`Failed to fetch FFmpeg asset ${url}: ${response.status}`);
  }
}

async function loadFFmpegCoreAssets(): Promise<CachedFFmpegCoreAssets> {
  const classWorkerURL = resolveAbsoluteAssetUrl(ffmpegClassWorkerURL);
  const coreURL = resolveAbsoluteAssetUrl(ffmpegCoreURL);
  const wasmURL = resolveAbsoluteAssetUrl(ffmpegCoreWasmURL);
  const workerURL = "data:text/javascript;base64,";
  const [coreData, wasmBinary] = await Promise.all([
    fetchAssetBuffer(coreURL),
    fetchAssetBuffer(wasmURL),
    warmAsset(classWorkerURL),
  ]);

  return {
    classWorkerURL,
    workerURL,
    coreData,
    wasmBinary,
  };
}

export function getFFmpegCoreUrls(): Promise<FFmpegCoreUrls> {
  if (!cachedCoreUrlsPromise) {
    cachedCoreUrlsPromise = loadFFmpegCoreAssets().catch((error) => {
      cachedCoreUrlsPromise = null;
      throw error;
    });
  }

  return cachedCoreUrlsPromise.then((assets) => ({
    classWorkerURL: assets.classWorkerURL,
    workerURL: assets.workerURL,
    // Return fresh buffers on each load because the worker transferables detach them.
    coreData: new Uint8Array(assets.coreData.slice(0)),
    wasmBinary: new Uint8Array(assets.wasmBinary.slice(0)),
  }));
}

export function warmFFmpegCore(): void {
  void getFFmpegCoreUrls();
}

export function scheduleFFmpegCoreWarmup(): void {
  if (warmupScheduled || typeof window === "undefined") {
    return;
  }

  warmupScheduled = true;

  const queueWarmup = () => {
    const runWarmup = () => {
      warmFFmpegCore();
    };

    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(runWarmup, { timeout: 3000 });
      return;
    }

    window.setTimeout(runWarmup, 0);
  };

  const scheduleAfterLoad = () => {
    window.setTimeout(queueWarmup, POST_LOAD_WARMUP_DELAY_MS);
  };

  if (document.readyState === "complete") {
    scheduleAfterLoad();
    return;
  }

  window.addEventListener("load", scheduleAfterLoad, { once: true });
}
